// src/utils/AuthenticationAttributionProcessor.tsx
import { now, offsetDateTimeToMs } from "@freightsimple/time/helpers";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// This component doesn't render anything - it just processes URL parameters
const AuthenticationAttributionProcessor: React.FC = () => {
  const location = useLocation();

  // oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
  useEffect(() => {
    try {
      // Extract attribution parameters from URL
      const params = new URLSearchParams(location.search);

      // Get all potential attribution parameters
      const utmSource = params.get("utm_source");
      const utmMedium = params.get("utm_medium");
      const utmCampaign = params.get("utm_campaign");
      const utmTerm = params.get("utm_term");
      const utmContent = params.get("utm_content");
      const gclid = params.get("gclid");
      const msclkid = params.get("msclkid");
      const oppref = params.get("oppref");
      const visitorId = params.get("fs_vid");
      const utmCampaignName = params.get("utm_campaign_name");
      const referrer = params.get("referrer");
      const landingPage = params.get("landing_page");

      // Check if we have any attribution data to store
      const hasAttributionData =
        utmSource ||
        utmMedium ||
        utmCampaign ||
        utmTerm ||
        utmContent ||
        gclid ||
        msclkid ||
        oppref ||
        visitorId ||
        utmCampaignName ||
        referrer ||
        landingPage;

      if (hasAttributionData) {
        // Cookie settings — 90-day max-age matches the Google Ads attribution window.
        // Secure flag is fine; we don't test attribution locally.
        const cookieOptions = ";path=/;max-age=7776000;SameSite=Lax;Secure";

        // Set cookies for each parameter that exists
        if (utmSource)
          document.cookie = `fs_src=${encodeURIComponent(utmSource)}${cookieOptions}`;
        if (utmMedium)
          document.cookie = `fs_med=${encodeURIComponent(utmMedium)}${cookieOptions}`;
        if (utmCampaign)
          document.cookie = `fs_cmp=${encodeURIComponent(utmCampaign)}${cookieOptions}`;
        if (utmTerm)
          document.cookie = `fs_trm=${encodeURIComponent(utmTerm)}${cookieOptions}`;
        if (utmContent)
          document.cookie = `fs_cnt=${encodeURIComponent(utmContent)}${cookieOptions}`;
        if (gclid)
          document.cookie = `fs_gid=${encodeURIComponent(gclid)}${cookieOptions}`;
        if (msclkid)
          document.cookie = `fs_mid=${encodeURIComponent(msclkid)}${cookieOptions}`;
        if (oppref)
          document.cookie = `fs_opr=${encodeURIComponent(oppref)}${cookieOptions}`;
        if (utmCampaignName)
          document.cookie = `fs_cmp_name=${encodeURIComponent(utmCampaignName)}${cookieOptions}`;
        if (referrer)
          document.cookie = `fs_ref=${encodeURIComponent(referrer)}${cookieOptions}`;
        if (landingPage)
          document.cookie = `fs_lp=${encodeURIComponent(landingPage)}${cookieOptions}`;

        // Set visitor ID or generate a new one
        if (visitorId) {
          document.cookie = `fs_vid=${visitorId}${cookieOptions}`;
        } else if (!getCookie("fs_vid")) {
          // Generate a new visitor ID if we don't have one
          const newVisitorId =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
          document.cookie = `fs_vid=${newVisitorId}${cookieOptions}`;
        }

        // Store in localStorage as backup
        try {
          const attrData = {
            src: utmSource || undefined,
            med: utmMedium || undefined,
            cmp: utmCampaign || undefined,
            trm: utmTerm || undefined,
            cnt: utmContent || undefined,
            gid: gclid || undefined,
            mid: msclkid || undefined,
            opr: oppref || undefined,
            cmp_name: utmCampaignName || undefined,
            ref: referrer || undefined,
            lp: landingPage || undefined,
            vid: visitorId || getCookie("fs_vid") || undefined,
            ts: offsetDateTimeToMs(now()),
          };

          localStorage.setItem("fs_data", JSON.stringify(attrData));
        } catch {
          // Silent fail for localStorage
        }
      }
    } catch (e) {
      console.warn("Error processing attribution parameters", e);
    }
  }, [location.search]); // Re-run when the URL search params change

  // Helper function to get a cookie value
  function getCookie(name: string): string | undefined {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)"),
    );
    const value = match?.[2];
    if (value !== undefined) return decodeURIComponent(value);
    return undefined;
  }

  // This component doesn't render anything
  return <></>;
};

export default AuthenticationAttributionProcessor;
